import { useFormContext, useWatch } from 'react-hook-form';
import {
  getOperatorRestrictions,
  getPatientDataFieldOperators,
} from '@aphp-ema/common/conditions';
import type { QuestionnaireFormData } from '@/features/questionnaires/types/questionnaire.interface';
import type { Question, QuestionType } from '@aphp-ema/common/questionnaire';
import type {
  PatientDataField,
  ConditionOperator,
} from '@aphp-ema/common/conditions';
import { useInitialQuestions } from '../useInitialQuestions';

export type TargetInfo =
  | {
      type: 'question';
      questionType: QuestionType;
      questionParameters: Question['parameters'];
    }
  | { type: 'patientData'; field: PatientDataField }
  | { type: 'none' };

export const useCondition = (questionIndex: number, conditionIndex: number) => {
  const { control } = useFormContext<QuestionnaireFormData>();

  const questions = useWatch({ control, name: 'questions', defaultValue: [] });
  const path = `questions.${questionIndex}.displayConditions` as const;
  const targetPath = `${path}.conditions.${conditionIndex}.target` as const;
  const target = useWatch({ control, name: targetPath });

  const initialQuestions = useInitialQuestions();

  const previous = questions.slice(0, questionIndex);

  const findQuestion = (targetId: string, source: string) => {
    if (source === 'question') {
      return previous.find((q) => q.id === targetId);
    }
    if (source === 'initial-question') {
      return initialQuestions.find((q) => q.id === targetId);
    }
    return undefined;
  };

  const question =
    target && 'questionId' in target && target.questionId
      ? findQuestion(target.questionId, target.source)
      : undefined;

  let operators: readonly ConditionOperator[] = [];
  let info: TargetInfo = { type: 'none' };

  if (
    (target?.source === 'question' || target?.source === 'initial-question') &&
    question
  ) {
    operators = getOperatorRestrictions(
      question.parameters.type,
      question.parameters,
    );
    info = {
      type: 'question',
      questionType: question.parameters.type,
      questionParameters: question.parameters,
    };
  } else if (target?.source === 'patientData' && target.field) {
    operators = getPatientDataFieldOperators(target.field);
    info = { type: 'patientData', field: target.field };
  }

  return {
    questions,
    previous,
    initialQuestions,
    target,
    question,
    operators,
    info,
    path,
    targetPath,
  };
};
